import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Package,
    Search,
    Plus,
    Tag,
    Loader,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    X,
    TrendingDown,
    MinusIcon,
    PlusIcon,
    PlusCircleIcon,
    MinusCircleIcon,
    ArrowDownIcon,
    ArrowUpIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import Common from '../../helper/common';
import { respEnum, StockLogTypeEnum } from '../../constants/appConstant';
import toast from 'react-hot-toast';

const StockManagement = () => {
    const searchInputRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(false);
    const [stockchanges,setStockChanges]=useState(true);
    const [stocktype,setStockType]=useState();

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [stockQuantity, setStockQuantity] = useState('');
    const [stockRemarks, setStockRemarks] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        let imageUrl = Common.Utility.getImagePath(imagePath);
        return imageUrl;
    };

    // Default category and product icons
    const getDefaultCategoryIcon = (categoryName) => {
        if (!categoryName || typeof categoryName !== 'string') return '📦';

        const icons = {
            'main': '🍽️',
            'appetizer': '🥗',
            'drinks': '🥤',
            'desserts': '🍰',
            'all': '🛍️'
        };
        return icons[categoryName?.toLowerCase()] || '📦';
    };

    const getDefaultProductIcon = (categoryName) => {
        if (!categoryName || typeof categoryName !== 'string') return '🍽️';

        const icons = {
            'main': '🍛',
            'appetizer': '🥗',
            'drinks': '🥤',
            'desserts': '🍰'
        };
        return icons[categoryName?.toLowerCase()] || '🛒';
    };

    // API calls
    const getCategories = async () => {
        try {
            const response = await Common.ApiService.getInstance().request(
                "GetProductCategories"
            );
            if (response?.status === respEnum.Success) {
                const categoryData = [
                    { id: 'all', name: 'All Categories', image: null },
                    ...response.data.map(cat => ({
                        id: cat.id,
                        name: cat.name,
                        image: cat.image || null
                    }))
                ];
                setCategories(categoryData);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([
                { id: 'all', name: 'All Categories', image: null },
                { id: 'main', name: 'Main', image: null },
                { id: 'appetizer', name: 'Starter', image: null },
                { id: 'drinks', name: 'Drinks', image: null },
                { id: 'desserts', name: 'Sweet', image: null }
            ]);
        }
    };

    const getProductsForPOS = async (categoryId = null) => {
        try {
            setProductsLoading(true);
            let url = 'GetProductsForPOS';
            if (categoryId !== 'all' && categoryId !== null) {
                url += `?categoryId=${categoryId}`;
            }

            const response = await Common.ApiService.getInstance().request(
                url,
                "GET"
            );

            if (response?.status === respEnum.Success) {
                const productData = response.data || [];
                setProducts(productData.map(product => ({
                    id: product.id,
                    name: product.name,
                    regular_price: product.regular_price || 0,
                    special_price: product.special_price || 0,
                    price: product.effectivePrice || product.regular_price || 0,
                    category: product.categoryid || 'main',
                    categoryName: product.categoryName || '',
                    stock: product.current_stock || 0,
                    image: product.thumbnail,
                    description: product.desc,
                    barcode: product.barcode,
                    isLowStock: product.isLowStock === 1,
                    sortOrder: product.sortOrder,
                    isdisplayontop: product.isdisplayontop
                })));
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
        } finally {
            setProductsLoading(false);
        }
    };

    const focusSearchInput = useCallback(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    // Initialize data
    useEffect(() => {
        const initializeData = async () => {
            setLoading(true);
            await Promise.all([
                getCategories(),
                getProductsForPOS()
            ]);
            setLoading(false);
        };
        initializeData();
    }, []);

    useEffect(() => {
        if (!loading) {
            focusSearchInput();
        }
    }, [loading, focusSearchInput]);

    // NOTE: kept intentionally simple — no auto-clear / auto-refresh timers.

    // Handle category change
    const handleCategoryChange = async (categoryId) => {
        setSelectedCategory(categoryId);
        await getProductsForPOS(categoryId);
    };

    // Filter products by search term only (category filtering is done by API)
    const filteredProducts = products
        .filter(p => {
            const q = `${searchTerm || ""}`.trim();
            if (!q) return true;

            const qLower = q.toLowerCase();
            const name = `${p?.name || ""}`.toLowerCase();
            const barcode = `${p?.barcode || ""}`.toLowerCase();
            return name.includes(qLower) || barcode.includes(qLower);
        })
        .sort((a, b) => {
            // Sort by displayontop first, then by sortOrder, then by name
            if (b.isdisplayontop !== a.isdisplayontop) {
                return b.isdisplayontop - a.isdisplayontop;
            }
            if (a.sortOrder !== b.sortOrder) {
                return (a.sortOrder || 999) - (b.sortOrder || 999);
            }
            return a.name.localeCompare(b.name);
        });

    // Handle add stock
    const handleAddStock = (product) => {
        setSelectedProduct(product);
        setStockQuantity('');
        setStockRemarks('');
        setIsModalOpen(true);
        setStockChanges(true);
        setStockType(StockLogTypeEnum.Stock_In);
    };

    // Handle subtract stock
        const handleSubtractStock = (product) => {
        setSelectedProduct(product);
        setStockQuantity('');
        setStockRemarks('');
        setIsModalOpen(true);
        setStockChanges(false);
        setStockType(StockLogTypeEnum.Stock_Out);

    };

    // Submit stock in
    const handleSubmitStockMovement = async () => {
        if (!stockQuantity || stockQuantity <= 0) {
            toast.error('Please enter a valid quantity');
            return;
        }

        setSubmitting(true);
        try {
            const response = await Common.ApiService.getInstance().request(
                "StockMovement",
                {
                    productid: selectedProduct.id,
                    productName: selectedProduct.name,
                    quantity: parseInt(stockQuantity),
                    type:stocktype,
                    remarks: stockRemarks
                },
                "POST"
            );

            if (response?.status === respEnum.Success) {
                toast.success('Stock updated successfully!');
                setIsModalOpen(false);
                // Refresh products to get updated stock
                await getProductsForPOS(selectedCategory);
            } else {
                toast.error(response?.message || 'Failed to update stock');
            }
        } catch (error) {
            console.error('Error updating stock:', error);
            toast.error('Failed to update stock');
        } finally {
            setSubmitting(false);
        }
    };

    // Utility functions
    const getStockColor = (stock, isLowStock = false) => {
        if (stock <= 0) return 'text-red-600';
        if (isLowStock || stock <= 10) return 'text-orange-500';
        if (stock <= 25) return 'text-yellow-600';
        return 'text-green-500';
    };

    const getStockStatus = (stock, isLowStock = false) => {
        if (stock <= 0) return { icon: X, color: 'text-red-600', text: 'Out of Stock' };
        if (isLowStock || stock <= 10) return { icon: AlertTriangle, color: 'text-orange-500', text: 'Low Stock' };
        if (stock <= 25) return { icon: AlertTriangle, color: 'text-yellow-600', text: 'Medium Stock' };
        return { icon: CheckCircle, color: 'text-green-500', text: 'In Stock' };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#2E55B9]"></div>
                    <p className="mt-4 text-lg font-medium text-gray-600">Loading Stock Management...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="bg-[#2E55B9] p-3 rounded-full">
                        <Package className="text-white" size={24} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Stock Management</h1>
                        <p className="text-gray-600">Manage product inventory and stock levels</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
                {/* Left Sidebar - Categories */}
                <div className="lg:col-span-1">
                    <Card className="shadow-xl border-0">
                        <CardHeader className="pb-3 bg-gradient-to-r from-[#2E55B9] to-[#4A73D6] text-white rounded-t-lg">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Tag size={18} />
                                Categories
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3">
                            <div className="grid grid-cols-1 gap-2">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        onClick={() => handleCategoryChange(cat.id)}
                                        disabled={productsLoading}
                                        className={`p-3 rounded-lg flex items-center gap-3 text-left transition-all duration-200 transform hover:scale-105 text-sm disabled:opacity-50
                      ${selectedCategory === cat.id
                                                ? 'bg-[#2E55B9] text-white shadow-lg scale-105'
                                                : 'bg-gray-50 hover:bg-[#2E55B9] hover:text-white border border-gray-200'}`}
                                    >
                                        {productsLoading && selectedCategory === cat.id ? (
                                            <Loader className="animate-spin" size={18} />
                                        ) : (
                                            <>
                                                {cat.image ? (
                                                    <img
                                                        src={getImageUrl(cat.image)}
                                                        alt={cat.name}
                                                        className="w-6 h-6 object-cover rounded"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'inline';
                                                        }}
                                                    />
                                                ) : null}
                                                <span className="text-lg" style={{ display: cat.image ? 'none' : 'inline' }}>
                                                    {getDefaultCategoryIcon(cat.name)}
                                                </span>
                                            </>
                                        )}
                                        <span className="font-medium truncate">{cat.name}</span>
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content - Products */}
                <div className="lg:col-span-5">
                    {/* Search Bar */}
                    <div className="mb-6">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#2E55B9]" size={20} />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search products..."
                                className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2E55B9] focus:ring-4 focus:ring-[#2E55B9]/20 transition-all duration-200 shadow-lg"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onBlur={() => {
                                    setTimeout(() => {
                                        focusSearchInput();
                                    }, 100);
                                }}
                            />
                            {searchTerm ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        focusSearchInput();
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500"
                                    aria-label="Clear search"
                                    title="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            ) : null}
                        </div>
                    </div>

                    {/* Products Grid */}
                    <Card className="shadow-xl border-0">
                        <CardContent className="p-6">
                            {productsLoading ? (
                                <div className="flex items-center justify-center py-20">
                                    <div className="text-center">
                                        <Loader className="animate-spin h-16 w-16 text-[#2E55B9] mx-auto mb-4" />
                                        <p className="text-gray-600 text-lg">Loading products...</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                                    {filteredProducts.map((product) => {
                                        const stockStatus = getStockStatus(product.stock, product.isLowStock);
                                        const StatusIcon = stockStatus.icon;

                                        return (
                                            <Card key={product.id} className="shadow-lg border-0 hover:shadow-xl transition-all duration-200 group">
                                                <CardContent className="p-3">
                                                    {/* Product Image/Icon */}
                                                    <div className="text-center mb-3">
                                                        <div className="w-16 h-16 mx-auto rounded-lg flex items-center justify-center bg-gray-50">
                                                            {product.image ? (
                                                                <img
                                                                    src={getImageUrl(product.image)}
                                                                    alt={product.name}
                                                                    className="w-16 h-16 object-cover rounded-lg shadow-md"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        if (e.target.nextSibling) {
                                                                            e.target.nextSibling.style.display = 'inline';
                                                                        }
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <span
                                                                className="text-4xl leading-none"
                                                                style={{ display: product.image ? 'none' : 'inline' }}
                                                            >
                                                                {getDefaultProductIcon(product.categoryName)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Product Info */}
                                                    <div className="text-center mb-3">
                                                        <h3 className="font-bold text-gray-800 text-sm mb-1 group-hover:text-[#2E55B9] transition-colors truncate">
                                                            {product.name}
                                                        </h3>

                                                        {/* Category */}
                                                        <div className="text-xs text-gray-600 mb-2">
                                                            {product.categoryName || 'Uncategorized'}
                                                        </div>

                                                        {/* Price */}
                                                        <div className="mb-2">
                                                            {product.special_price > 0 ? (
                                                                <div className="space-y-1">
                                                                    <div className="text-xs text-gray-400 line-through">
                                                                        PKR {product.regular_price}
                                                                    </div>
                                                                    <div className="text-sm font-bold text-green-600">
                                                                        PKR {product.special_price}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-sm font-bold text-blue-600">
                                                                    PKR {product.regular_price}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Stock Status */}
                                                        <div className={`flex items-center justify-center gap-1 mb-2 ${stockStatus.color}`}>
                                                            <StatusIcon size={14} />
                                                            <span className="font-medium text-xs">{stockStatus.text}</span>
                                                        </div>

                                                        {/* Stock Quantity */}
                                                        <div className={`text-lg font-bold mb-3 ${getStockColor(product.stock, product.isLowStock)}`}>
                                                            {product.stock}
                                                            <span className="text-xs font-normal text-gray-500 ml-1">units</span>
                                                        </div>
                                                    </div>

                                                    {/* Add Stock Button */}
                                                    <div className="flex items-center gap-1">

                                                    <Button
                                                        onClick={() => handleAddStock(product)}
                                                        className="w-full bg-gradient-to-r from-[#2E55B9] to-[#4A73D6] hover:from-[#2E55B9]/90 hover:to-[#4A73D6]/90 text-white font-medium py-1 rounded-lg transition-all duration-200 flex items-center justify-center gap-1 shadow-lg text-xs"
                                                        >
                                                        <TrendingUp size={9} />
                                                        In
                                                        {/* <ArrowDownIcon  className="w-4 h-4" /> */}
                                                    </Button>

                                                     <Button
                                                        onClick={() => handleSubtractStock(product)}
                                                        className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-600/90 hover:to-red-500/90 text-white font-medium py-1 rounded-lg transition-all duration-200 flex items-center justify-center gap-1 shadow-lg text-xs"
                                                    >
                                                        <TrendingDown size={9} />
                                                        Out
                                                         {/* <ArrowUpIcon  className="w-3 h-3" /> */}
                                                    </Button>
                                                        </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            )}

                            {!productsLoading && filteredProducts.length === 0 && (
                                <div className="text-center py-20">
                                    <Package size={80} className="mx-auto mb-4 text-gray-400" />
                                    <h3 className="text-xl font-medium text-gray-600 mb-2">No products found</h3>
                                    <p className="text-gray-500">Try adjusting your search or category filter</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Add Stock Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen} onChange={stockchanges}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                      {stockchanges ? (
                            <>
                                <TrendingUp className="text-[#2E55B9]" size={20} />
                                Add Stock
                            </>
                            ) : (
                            <>
                                <TrendingDown className="text-[#2E55B9]" size={20} />
                                Subtract Stock
                            </>
                        )}

                        </DialogTitle>
                    </DialogHeader>

                    {selectedProduct && (
                        <div className="space-y-4">
                            {/* Product Info */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex items-center gap-3 mb-3">
                                    {selectedProduct.image ? (
                                        <img
                                            src={getImageUrl(selectedProduct.image)}
                                            alt={selectedProduct.name}
                                            className="w-12 h-12 object-cover rounded-lg"
                                        />
                                    ) : (
                                        <span className="text-3xl">
                                            {getDefaultProductIcon(selectedProduct.categoryName)}
                                        </span>
                                    )}
                                    <div>
                                        <h3 className="font-bold text-gray-800">{selectedProduct.name}</h3>
                                        <p className="text-sm text-gray-600">{selectedProduct.categoryName}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Current Stock:</span>
                                        <span className={`font-bold ml-2 ${getStockColor(selectedProduct.stock, selectedProduct.isLowStock)}`}>
                                            {selectedProduct.stock} units
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Price:</span>
                                        <span className="font-bold ml-2 text-green-600">
                                            PKR {selectedProduct.price}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Quantity Input */}
                            <div>
                               {stockchanges ? (
                                <>
                                    <Label htmlFor="quantity" className="text-sm font-medium">
                                        Quantity to Add *
                                    </Label>
                                </>
                                ) : (
                                <>
                                     <Label htmlFor="quantity" className="text-sm font-medium">
                                        Quantity to Subtract *
                                    </Label>
                                </>
                                )}

                                {/* <Label htmlFor="quantity" className="text-sm font-medium">
                                    Quantity to Add *
                                </Label> */}
                                <Input
                                    id="quantity"
                                    type="number"
                                    placeholder="Enter quantity"
                                    value={stockQuantity}
                                    onChange={(e) => setStockQuantity(e.target.value)}
                                    min="1"
                                    className="mt-1"
                                />
                            </div>

                            {/* Remarks Input */}
                            <div>
                                <Label htmlFor="remarks" className="text-sm font-medium">
                                    Remarks (Optional)
                                </Label>
                                <Textarea
                                    id="remarks"
                                    placeholder="Add any notes about this stock addition..."
                                    value={stockRemarks}
                                    onChange={(e) => setStockRemarks(e.target.value)}
                                    className="mt-1"
                                    rows={3}
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsModalOpen(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        
                        <Button
                            onClick={handleSubmitStockMovement}
                            disabled={submitting || !stockQuantity || stockQuantity <= 0}
                            className={`${
                            stockchanges ? "bg-[#2E55B9] hover:bg-[#2E55B9]/90" : "bg-[#FF0000] hover:bg-[#FF0000]/90"
                            }`}
                        >
                          {submitting ? (
                                <>
                                    <Loader className="animate-spin mr-2" size={16} />
                                    Updating...
                                </>
                                ) : 
                                (
                                <>
                                    {stockchanges? (
                                        <>
                                            <TrendingUp size={16} className="mr-2" />
                                            Add Stock
                                        </>
                                    ) : (
                                        <>
                                            <TrendingDown size={16} className="mr-2" />
                                            Subtract Stock
                                        </>
                                    )}
                                </>
                            )}

                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StockManagement; 